<template>
  <v-form ref="form">
    <v-row dense>
      <v-col cols="6">
        <v-text-field
          v-model="action.name"
          :rules="rules.name"
          clearable
          dense
          outlined
          :label="$t('commons.name')"
        />
      </v-col>
      <v-col cols="6">
        <v-select
          v-model="action.type_action"
          :items="actionTypes"
          :rules="rules.type_action"
          dense
          outlined
          :label="$t('commons.type')"
        />
      </v-col>
      <!-- <v-col cols="12">
        <v-textarea
          v-model="action.description"
          dense
          outlined
          :label="$t('commons.description')"
        />
      </v-col> -->
      <v-col cols="12">
        <label>{{ $t("commons.description") }}</label>
        <ckeditor
          :editor="editor"
          tag-name="textarea"
          rows="3"
          v-model="action.description"
          :config="editorConfig"
          :label="$t('commons.description')"
          class="content-element"
        ></ckeditor>
      </v-col>
      <v-col cols="4">
        <v-select
          v-model="action.user"
          :items="users"
          :rules="rules.user"
          dense
          outlined
          item-text="email"
          item-value="id"
          :label="$t('commons.intended_for')"
        />
      </v-col>
      <v-col cols="4">
        <DatetimePicker
          v-model="action.end_date"
          :min="cfsi.created_at"
          :textFieldProps="{
            dense: true,
            label: $t('commons.date.end_date'),
            rules: rules.endDate
          }"
        ></DatetimePicker>
      </v-col>
      <v-col cols="4">
        <v-select
          v-model="action.status"
          :items="statusItems"
          :rules="rules.status"
          dense
          outlined
          :label="$t('commons.status.name')"
        ></v-select>
      </v-col>
    </v-row>
    <v-row class="pb-4 pr-4 d-flex align-center justify-end">
      <avs-button
        block
        color="primary white--text"
        class="mr-4"
        noSpantext
        :disabled="isDisabled()"
        @click="sign(action.id)"
      >
        <template v-slot:name>
          <span class="mdi mdi-draw"></span>
          {{ $t("commons.sign") }}
        </template>
      </avs-button>
      <template v-if="saving">
        <v-progress-circular
          indeterminate
          size="20"
          color="primary"
        ></v-progress-circular>
      </template>
      <avs-saveButton
        v-else
        :disabled="isSaved || isSign || isClosed"
        @click="handleSave"
      />
    </v-row>
  </v-form>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import { required } from "@/utils/validator"
import DatetimePicker from "@/components/inputs/DatetimePicker.vue"
import TeamService from "@/services/TeamService"
import CfsiService from "@/services/CfsiService"
import { STATUS_ITEMS, STATUS_ITEMS_DISABLED } from "@/utils/consts"
import { isInspector } from "@/utils/middleware"
export default {
  components: {
    DatetimePicker
  },
  name: "CfsiAction",
  props: {
    value: {
      type: Object,
      required: true
    },
    isSaved: {
      type: Boolean,
      default: false
    },
    cfsi: {
      type: Object,
      required: true
    },
    projectId: {
      type: String,
      required: true
    }
  },
  created() {
    this.fetchUser()
    this.checkIfActionIsSigned()
  },
  data() {
    return {
      action: { ...this.value, description: this.value.description || "" },
      actionTypes: ["Preventive", "Corrective"],
      rules: {
        endDate: [required],
        name: [required],
        cfsi: [required],
        type_action: [required],
        status: [required],
        user: [required]
      },
      users: [],
      saving: false,
      editor: ClassicEditor,
      editorConfig: {
        toolbar: ["heading", "|", "bold", "italic", "link", "|", "undo", "redo"]
      },
      isClosed: false,
      isSign: false
    }
  },
  computed: {
    statusItems() {
      return isInspector(this.projectRole) ? STATUS_ITEMS : STATUS_ITEMS_DISABLED
    },
    projectRole() {
      return this.$store.state.app.project.role
    }
  },
  methods: {
    async fetchUser() {
      const response = await TeamService.getTeam(this, this.projectId)
      this.users = response
    },
    async updateAction() {
      this.saving = true
      this.action.lang = this.$i18n.locale
      return CfsiService.updateAction(this.action.id, this.action)
        .then(response => {
          this.$emit("update", response)
          this.send(
            this.$t("messages.success.updated", { entity: this.$t("commons.action") }),
            "success"
          )
          this.saving = false
          if (this.action.status == "Closed") {
            this.isClosed = true
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },
    async createAction() {
      this.saving = true
      this.action.lang = this.$i18n.locale
      return CfsiService.createAction(this.cfsi.id, this.action)
        .then(response => {
          this.$emit("update", response)
          this.send(
            this.$t("messages.success.saved", { entity: this.$t("commons.action") }),
            "success"
          )
          this.saving = false
          if (this.action.status == "Closed") {
            this.isClosed = true
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            // alert("Access denied: You do not have permission to access this resource.")
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },
    handleSave() {
      if (this.validate()) {
        const p = this.action.id ? this.updateAction() : this.createAction()
        p.then(e => {
          this.$emit("isSaved", true)
        })
      }
    },
    isDisabled() {
      return this.isClosed || this.isSign || !this.action.id
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    validate() {
      return this.$refs.form.validate()
    },
    sign(actionId) {
      // Si aucune image, appelez simplement la fonction post avec l'ID du document
      CfsiService.getActionSignatures(actionId)
        .then(r => {
          CfsiService.postActionSignature(actionId).then(v => {
            this.$emit("isSigned", true)
            this.isSigned = true
            this.isClosed = true
          })
        })
        .catch(e => {
          if (e.response && e.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            this.$emit("error", e)
          }
        })
    },
    async checkIfActionIsSigned() {
      if (this.action.id) {
        try {
          const signatures = await CfsiService.getActionSignatures(this.action.id)
          if (signatures && signatures.length > 0) {
            this.isSign = true
            this.isClosed = true
          }
        } catch (err) {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          } else {
            this.$emit("error", err)
          }
        }
      }
    }
  },
  watch: {
    action: {
      deep: true,
      handler(newValue, oldValue) {
        this.$emit("isSaved", false)
      }
    }
  }
}
</script>
